// AI-GENERATED | 365 Copilot (GPT-5) | Tarkistanut Oskari Rintamäki | 2026-04-10
// Renders GFI feature results either as a ka-table (table view) or as expandable list items (card view).
import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo
} from 'react';
import styled from 'styled-components';
import FeatureDataTabContentItem from './FeatureDataTabContentItem';
import strings from '../../../translations';
import { ReactReduxContext } from 'react-redux';
import { faTable, faList, faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppSelector } from '../../../state/hooks';
import { setFilteringInfo } from '../../../state/slices/rpcSlice';
import { Table } from 'ka-table';
import 'ka-table/style.scss';
import { theme, isMobile } from '../../../theme/theme';
import { Tooltip } from 'react-tooltip';
import { setMinimizeFilterDialog } from '../../../state/slices/uiSlice';
import { SortingMode, PagingPosition } from 'ka-table/enums';
import { renderLinksInText } from '../../../utils/commonUtil';
import FeatureActionsMenu from '../tools/FeatureActionsMenu';
import { IS_EXTRANET } from '../../../utils/appInfoUtil';

const StyledSelectedTabHeader = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 2px 2px 4px 0px rgb(0 0 0 / 20%);
  z-index: 2;
`;

const StyledSelectedTabTitle = styled.div`
  padding: 8px;
  p {
    color: ${(props) => props.theme.colors.mainColor1};
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
`;

const StyledSelectedTabDisplayOptionsButton = styled.div`
  position: relative;
  right: 0px;
  padding: 8px;
  cursor: pointer;
  color: ${(props) => props.theme.colors.mainColor1};
  svg {
    font-size: 24px;
  }
`;

const StyledTabContent = styled.div`
  overflow: auto;
  td:nth-child(odd) {
    border-right: 1px solid #ddd;
  }
  tr:nth-child(2n) {
    background-color: #f2f2f2;
  }
  table tr {
    border-bottom: 1px solid #ddd;
  }
`;

const StyledDataTableWrapper = styled.div`
  overflow: auto;
  /* Transient row highlight on hover (mirrors FilteredFeatureResults) */
  .ka-tr:hover,
  .ka-tr:hover .ka-cell {
    background-color: ${(props) => props.theme.colors.mainColor3transparent30};
  }
`;

/* ------------------------------------------------------------------ */
/* Table helpers                                                       */
/* ------------------------------------------------------------------ */

// Flattens a feature into a row object for ka-table.
function flattenFeatureForTable(feature) {
  if (feature?.properties) {
    return { ...feature.properties, id: feature.id };
  }
  if (feature?.geojson) {
    return { ...feature.geojson, id: feature.id };
  }
  return feature;
}

// Builds basic columns from a row object.
function getColumnsFromRow(row) {
  if (!row) return [];
  return Object.keys(row)
    .filter((k) => k !== 'id' && k !== 'UID')
    .map((key) => ({
      key,
      title: key,
      width: 180,
      colGroup: { style: { minWidth: 120 } }
    }));
}

// AI-GENERATED | 365 Copilot (GPT-5) | Tarkistanut Oskari Rintamäki | 2026-04-13
function ensureActionsColumn(columns, reports) {
  const cols = Array.isArray(columns) ? [...columns] : [];

  if (reports) {
    const alreadyExists = cols.some((c) => c.key === '__actions');

    if (!alreadyExists) {
      cols.push({
        key: '__actions',
        title: strings.gfi.actions.title,
        width: 120,
        colGroup: {
          style: {
            minWidth: '100px',
            width: '120px'
          }
        },
        isSortable: false
      });
    }

  }

  return cols;
}

/* ------------------------------------------------------------------ */
/* Userlayer table props builder                                       */
/* ------------------------------------------------------------------ */

function initUserLayerTableProps(inputFeatures) {
  const rows = Array.isArray(inputFeatures) ? inputFeatures : [];
  const flatRows = rows.map(flattenFeatureForTable);

  let columnsArray = [];
  if (flatRows.length > 0) {
    columnsArray = getColumnsFromRow(flatRows[0]);
  }

  return {
    columns: columnsArray,
    filterableColumns: [],
    filteredFeatures: rows, // For card/list rendering
    data: flatRows, // For ka-table
    rowKeyField: 'id',
    sortingMode: SortingMode.SingleTripleState,
    columnResizing: true,
    paging: {
      enabled: true,
      pageIndex: 0,
      pageSize: 100,
      pageSizes: [10, 50, 100],
      position: PagingPosition.Bottom
    },
    // Render normal cells with renderLinksInText, actions cell with buttons
    childComponents: {
      cellText: {
        content: ({ column, rowData, value }) => {
          const cellValue = value !== undefined ? value : rowData?.[column.key];
          return renderLinksInText(cellValue);
        }
      }
    }
  };
}

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

const FeatureDataTabContent = ({ layer, title, tablePropsInit }) => {
  const filteringInfo = useAppSelector((state) => state.rpc.filteringInfo);
  const filters = useAppSelector((state) => state.rpc.filters);
  const channel = useAppSelector((state) => state.rpc.channel);
  const { store } = useContext(ReactReduxContext);
  const minimizeFilter = useAppSelector((state) => state.ui.minimizeFilter);
  const [showDataTable, setShowDataTable] = useState(false);

  const reports = useMemo(
    () => {
      if (!IS_EXTRANET) return null;
      if (layer.config?.reports && layer.config?.reports.length > 0) {
        return layer.config?.reports;
      } else {
        return null;
      }
    },
    [layer]
  );

  const isUserLayer =
    typeof layer?.id === 'string' && layer.id.startsWith('userlayer_');

  // AI-GENERATED | 365 Copilot (GPT-5) | Tarkistanut Oskari Rintamäki | 2026-04-10
  // Unconditional useMemo: derive features + tableProps safely for both userlayers and normal layers.
  const { features, tableProps } = useMemo(() => {
    const derivedFeatures = tablePropsInit?.filteredFeatures ?? [];

    if (isUserLayer) {
      const props = initUserLayerTableProps(derivedFeatures, layer, title);
      return { features: derivedFeatures, tableProps: props };
    }

    // Normal layer: clone base props, ensure actions column, and set childComponents for cell rendering.
    const base = tablePropsInit || {};

    const cols = ensureActionsColumn(base.columns || [], reports);

    const props = {
      ...base,
      columns: cols,
      childComponents: {
        cellText: {
          content: ({ column, rowData }) => {
            if (column.key === '__actions') {

              return (
                <FeatureActionsMenu
                  reports={layer.config?.reports || null}
                  data={base}
                  ariaLabel={`Actions for ${rowData?.id}`}
                  disableTooltip={true}
                />
              );
            }
          }
        }
      },
      rowKeyField: base.rowKeyField || 'id',
      paging: base.paging || {
        enabled: true,
        pageIndex: 0,
        pageSize: 100,
        pageSizes: [10, 50, 100],
        position: PagingPosition.Bottom
      },
      sortingMode: base.sortingMode || SortingMode.SingleTripleState,
      columnResizing: base.columnResizing ?? true
    };

    return { features: derivedFeatures, tableProps: props };
  }, [isUserLayer, tablePropsInit, layer, title, reports]);

  const selectFeature = tablePropsInit?.selectFeature;
  const deSelectFeature = tablePropsInit?.deSelectFeature;

  const featureById = useMemo(() => {
    const map = new Map();
    features.forEach((f) => {
      if (f?.id != null) map.set(f.id, f);
    });
    return map;
  }, [features]);

  /* ---------------- Filtering logic ---------------- */

  const activeFilteringOnLayer = useCallback(() => {
    return filters.some((filter) => filter.layer === layer.id);
  }, [filters, layer.id]);

  const [isActiveFiltering, setIsActiveFiltering] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);

  useEffect(() => {
    setIsActiveFiltering(activeFilteringOnLayer());
  }, [filters, activeFilteringOnLayer]);

  useEffect(() => {
    setIsFiltering((tableProps?.filterableColumns?.length ?? 0) > 0);
  }, [tableProps]);

  const handleFilterClick = () => {
    if (!filteringInfo.some((f) => f.layer.id === layer.id)) {
      if (filteringInfo.filter((f) => f.layer.id === layer.id).length === 0) {
        const filterColumnsArray = [];
        layer.config?.gfi?.filterFields &&
          layer.config?.gfi?.filterFields.forEach((column) => {
            if (column.field && column.type) {
              filterColumnsArray.push({
                key: column.field,
                title: column.field,
                type: column.type,
                default: column.default ?? false
              });
            }
          });

        const updateFilter = [...filteringInfo];
        updateFilter.push({
          dialogOpen: true,
          layer: {
            id: layer.id,
            title: layer.name,
            filterFieldsInfo: layer.config?.gfi?.filterFieldsInfo ?? null,
            filterColumnsArray: filterColumnsArray
          }
        });
        store.dispatch(setFilteringInfo(updateFilter));
      }
    }

    minimizeFilter &&
      store.dispatch(
        setMinimizeFilterDialog({ minimized: false, layer: layer.id })
      );
  };

  return (
    <>
      <StyledSelectedTabHeader>
        <StyledSelectedTabTitle>
          <p>{title.toUpperCase()}</p>
        </StyledSelectedTabTitle>

        {isFiltering && (
          <StyledSelectedTabDisplayOptionsButton
            onClick={handleFilterClick}
            id="gfi_filter_button"
          >
            <Tooltip
              style={{ backgroundColor: theme.colors.mainColor1 }}
              hidden={isMobile}
              anchorSelect="#gfi_filter_button"
              id="gfi_filter_tooltip"
              place="bottom"
              effect="float"
            >
              <span>{strings.gfifiltering.filter}</span>
            </Tooltip>
            <FontAwesomeIcon
              icon={faFilter}
              style={{
                color:
                  filters && isActiveFiltering
                    ? theme.colors.secondaryColorPink
                    : theme.colors.mainColor1
              }}
            />
          </StyledSelectedTabDisplayOptionsButton>
        )}

        <StyledSelectedTabDisplayOptionsButton
          onClick={() => setShowDataTable(!showDataTable)}
        >
          <FontAwesomeIcon icon={showDataTable ? faList : faTable} />
        </StyledSelectedTabDisplayOptionsButton>
      </StyledSelectedTabHeader>

      {showDataTable ? (
        <StyledDataTableWrapper>
          <Table
            {...tableProps}
            childComponents={{
              ...tableProps.childComponents,
              dataRow: {
                elementAttributes: ({ rowData }) => {
                  const feature = rowData?.__feature ?? featureById.get(rowData?.id);
                  return {
                    onMouseEnter: () => {
                      if (selectFeature && feature) {
                        selectFeature(channel, [feature]);
                      }
                    },
                    onMouseLeave: () => {
                      if (deSelectFeature) {
                        deSelectFeature(channel);
                      }
                    }
                  };
                }
              }
            }}
          />
        </StyledDataTableWrapper>
      ) : (
        <StyledTabContent>
          {features.map((feature, index) => {
            let showTitle = feature.id ?? `${title} #${index}`;

            if (
              feature.id &&
              Array.isArray(feature.id.split('.')) &&
              feature.id.split('.')[1]
            ) {
              showTitle = `${strings.gfi.uniqueId} ` + feature.id.split('.')[1];
            } else {
              showTitle = title + ' ' + (feature.id ?? index + 1);
            }

            return (
              <FeatureDataTabContentItem
                layer={layer}
                key={feature.id ?? index}
                title={showTitle}
                data={feature}
                index={index}
                contentIndex={index}
                selectFeature={selectFeature}
                deSelectFeature={deSelectFeature}
                reports={reports}
              />
            );
          })}
        </StyledTabContent>
      )}
    </>
  );
};

export default FeatureDataTabContent;
